import {
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart as RechartsRadarChart,
} from "recharts";

import { ChartLegend, ChartLegendContent } from "@/components/ui/ChartLegend";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColorCssVarKey,
} from "@/components/ui/ChartTooltip";
import type { RadarDatum } from "@/components/ui/radar-chart/radarChartUtils";
import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import { cn } from "@/lib/utils";
import { resolveChartShowLegend } from "@/lib/utils/resolveChartShowLegend";

export type { RadarDatum };

/** One numeric series column, or several overlaid series (e.g. comparing feeds). */
export type RadarSeriesProps =
  | {
      /** Single series: numeric column in `data`. */
      dataKey: string;
      dataKeys?: never;
    }
  | {
      dataKey?: never;
      /** One or more series; each `key` is a numeric column in `data`. */
      dataKeys: { key: string; label: string }[];
    };

export type RadarChartViewProps = {
  data: RadarDatum[];
  chartConfig: ChartConfig;
  /** Field for {@link PolarAngleAxis} labels (each row = one spoke). */
  angleKey: string;
  title?: string | null;
  height?: number | null;
  /**
   * When omitted: show legend only if there are two or more series (`dataKeys`).
   * @default undefined (auto)
   */
  showLegend?: boolean;
  className?: string;
  tooltipCompact?: boolean;
  tooltipHideLabel?: boolean;
  /** Single-series default matches chat radar: light fill, strong stroke. @default 0.2 */
  fillOpacity?: number;
  /** @default 2 */
  strokeWidth?: number;
  isAnimationActive?: boolean;
  /** @default true */
  accessibilityLayer?: boolean;
} & RadarSeriesProps;

const RadarChartEmpty = () => (
  <div className="flex w-full min-w-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xxs text-muted-foreground">
    No data to display
  </div>
);

const buildSingleSeriesList = (
  dataKey: string,
  chartConfig: ChartConfig,
): { key: string; label: string }[] => {
  const L = chartConfig[dataKey]?.label;
  const label = typeof L === "string" ? L : String(L ?? dataKey);
  return [{ key: dataKey, label }];
};

/**
 * Radar chart: {@link ChartContainer}, compact tooltip, legend — same integration pattern as {@link PieDonutChart}.
 * Pass `dataKey` for one series or `dataKeys` to overlay multiple series (e.g. compare feeds).
 */
const RadarChartView = (props: RadarChartViewProps) => {
  const {
    data,
    chartConfig,
    angleKey,
    title = null,
    height = null,
    showLegend: showLegendProp,
    className,
    tooltipCompact = true,
    tooltipHideLabel = false,
    fillOpacity = 0.2,
    strokeWidth = 2,
    isAnimationActive = true,
    accessibilityLayer = true,
  } = props;

  const inReportContext = useReportTheme() != null;
  const multiKeys =
    "dataKeys" in props &&
    props.dataKeys !== undefined &&
    props.dataKeys.length > 0
      ? props.dataKeys
      : null;
  const multi = multiKeys != null;

  const seriesList: { key: string; label: string }[] = multiKeys
    ? multiKeys.map((s) => ({ key: s.key, label: s.label }))
    : "dataKey" in props && props.dataKey != null && props.dataKey !== ""
      ? buildSingleSeriesList(props.dataKey, chartConfig)
      : [];

  const hasValues =
    seriesList.length > 0 &&
    data.length > 0 &&
    data.some((row) =>
      seriesList.some((s) => {
        const v = row[s.key];
        const n = typeof v === "number" ? v : Number(v);
        return !Number.isNaN(n);
      }),
    );

  const showLegend = resolveChartShowLegend(
    showLegendProp,
    seriesList.length,
    inReportContext,
    true,
  );

  if (!hasValues) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {title != null && title !== "" ? (
          <p className="m-0 text-xs font-medium text-foreground">{title}</p>
        ) : null}
        <RadarChartEmpty />
      </div>
    );
  }

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-1.5"
      style={height != null ? { minHeight: height, height } : undefined}
    >
      {title != null && title !== "" ? (
        <p className="m-0 text-xs font-medium text-foreground">{title}</p>
      ) : null}
      <ChartContainer
        config={chartConfig}
        className={cn(
          "mx-auto aspect-square w-full justify-center",
          height == null ? "max-h-[240px]" : "max-h-[min(280px,100%)]",
          className,
        )}
      >
        <RechartsRadarChart data={data} accessibilityLayer={accessibilityLayer}>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                compact={tooltipCompact}
                hideLabel={tooltipHideLabel}
                nameKey={angleKey}
              />
            }
          />
          <PolarAngleAxis dataKey={angleKey} />
          <PolarGrid />
          {seriesList.map((s) => {
            const seriesColorVar = `var(--color-${chartColorCssVarKey(s.key)})`;
            return (
              <Radar
                key={s.key}
                name={s.label}
                dataKey={s.key}
                fill={seriesColorVar}
                fillOpacity={fillOpacity}
                stroke={seriesColorVar}
                strokeWidth={strokeWidth}
                isAnimationActive={isAnimationActive}
              />
            );
          })}
          {showLegend ? (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <ChartLegendContent
                  nameKey={multi ? "dataKey" : undefined}
                  className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300"
                />
              }
            />
          ) : null}
        </RechartsRadarChart>
      </ChartContainer>
    </div>
  );
};

export default RadarChartView;
